/* GNUv3 License

Copyright (c) Jacob K. F. Bogers <jkfbogers@gmail.com>

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

*/
import * as debug from 'debug';

import { M_LN_SQRT_2PI, ML_ERR_return_NAN } from '../common/_general';

import { gammafn } from '../gamma/gamma_fn';
import { lgammafn } from '../gamma/lgamma_fn';
import { lgammacor } from '../gamma/lgammacor';
import { multiplexer }  from '../r-func';

const { log, log1p } = Math;

const {
  isNaN: ISNAN,
  isFinite: R_FINITE,
  POSITIVE_INFINITY: ML_POSINF,
  NEGATIVE_INFINITY: ML_NEGINF
} = Number;

const printer = debug('lbeta');

export function lbeta(_a: number | number[], _b: number|number[]): number|number[] {
  return multiplexer(_a, _b)((a, b) => internal_lbeta(a, b));
} 

export function internal_lbeta(a: number, b: number): number {
  let corr: number;
  let p: number;
  let q: number;

  if (ISNAN(a) || ISNAN(b)) return a + b;
  p = q = a;
  if (b < p) p = b; // := min(a,b)
  if (b > q) q = b; // := max(a,b)

  // both arguments must be >= 0
  if (p < 0) return ML_ERR_return_NAN(printer);
  else if (p === 0) {
    return ML_POSINF;
  } else if (!R_FINITE(q)) {
    // q == +Inf
    return ML_NEGINF;
  }

  if (p >= 10) {
    // p and q are big.
    corr = lgammacor(p) + lgammacor(q) - lgammacor(p + q);
    return (
      log(q) * -0.5 +
      M_LN_SQRT_2PI +
      corr +
      (p - 0.5) * log(p / (p + q)) +
      q * log1p(-p / (p + q))
    );
  } else if (q >= 10) {
    // p is small, but q is big.
    corr = lgammacor(q) - lgammacor(p + q);
    return (
      lgammafn(p) +
      corr +
      p -
      p * log(p + q) +
      (q - 0.5) * log1p(-p / (p + q))
    );
  } else {
    // p and q are small: p <= q < 10.
    // R change for very small args

    //NOTE FROM JACOB, funny how only p is tested and not q?? doesnt make sense
    //  since the beta function is "symetric" for p and q aka beta(p,q) = beta (q,p)

    // removed  if (p < 1e-306) return lgamma(p) + (lgamma(q) - lgamma(p+q));
    //else
    return log(gammafn(p) * (gammafn(q) / gammafn(p + q)));
  }
}



// WEBPACK FOOTER //
// ./src/lib/beta/lbeta.ts